/**
 * Seção "Medições" da janela de dados do risco (#606, E3): uma tabela editável com data,
 * aparelho, tipo de avaliação, valor, unidade, técnica e limite aplicado — os dois últimos o
 * eSocial e o LTCAT pedem. Os campos que faltam na medição que conta (a mais recente) ficam
 * destacados.
 *
 * O enquadramento (grau da NR-15, anexo e quantidades da NR-16) só aparece quando o risco é dessa
 * natureza, e vale para a medição mais recente — é ela que os documentos e a completude leem.
 */

import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { visuallyHidden } from '@mui/utils';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  MEASUREMENT_ASSESSMENT_TYPES,
  MEASUREMENT_ASSESSMENT_TYPE_LABELS,
  NR16_ANNEXES,
  type FormulaDoLimiteParaTela,
} from '../../../types/environmentalMeasurement';
import { UNHEALTHY_DEGREES, OCC_UNHEALTHY_DEGREE_LABELS, type UnhealthyDegree } from '../../../types/occupationalRisk';
import type { FormularioDoRisco, MeasurementFormRow } from '../RiskApplicationModal';
import { ALVO, alvoDaMedicao, indiceDaMedicaoMaisRecente } from '../completudeDoRisco';
import { hojeNoSeletor } from '../diaDoCampo';
import { BlocoDoLimiteCalculado } from '../../measurements/BlocoDoLimiteCalculado';

export interface OpcaoDeAparelho {
  id: number;
  name: string;
  serialNumber?: string | null;
  /** O aparelho foi usado antes, mas foi excluído do cadastro. */
  orphaned?: boolean;
}

interface SecaoMedicoesProps {
  opcoesDeAparelho: OpcaoDeAparelho[];
  unidadePadrao: string;
  desabilitada: boolean;
  perigoso: boolean;
  insalubre: boolean;
  /** Grau do catálogo: sugerido ao marcar o enquadramento, nunca imposto (#357). */
  grauPadrao: string;
  /** O nome do risco, para dizer por que o enquadramento aparece. */
  nomeDoRisco: string;
  /** Os `id` dos campos que faltam para a completude — ficam destacados. */
  faltando: ReadonlySet<string>;
  /** Sílica livre (#459): com a fórmula, o limite sai do % de quartzo e o campo digitado não existe. */
  formulaDoLimite?: FormulaDoLimiteParaTela | null;
}

/** O grau no catálogo é texto livre ("0.2" chega da planilha): só sugere o que o servidor aceita. */
const grauSugerido = (grauDoCatalogo: string): string =>
  UNHEALTHY_DEGREES.includes(grauDoCatalogo as UnhealthyDegree) ? grauDoCatalogo : '';

const NOVA_MEDICAO: MeasurementFormRow = {
  medicaoId: null,
  measurementEquipmentId: null,
  value: null,
  unit: '',
  measuredAt: null,
  observations: '',
  technique: '',
  appliedToleranceLimit: '',
  quartzPercent: '',
  assessmentType: '',
  storedQuantity: null,
  storedQuantityUnit: '',
  transportedQuantity: null,
  transportedQuantityUnit: '',
  riskArea: '',
  storageType: '',
  hazardousFraming: false,
  hazardousAnnex: '',
  unhealthyFraming: false,
  unhealthyDegreeApplied: '',
};

/** O tipo de avaliação precisa de 170px para caber "Quantitativa"; a lixeira fica numa coluna fixa no fim. */
const COLUNAS = '150px minmax(160px, 1.4fr) minmax(170px, 1fr) 90px 90px minmax(110px, 1fr) minmax(110px, 1fr) 48px';

/**
 * A soma das larguras mínimas das colunas (928px), dos sete vãos de 8px e do respiro lateral da
 * linha (8px + 16px): abaixo disso, o contêiner rola na horizontal em vez de espremer os campos.
 */
const LARGURA_MINIMA_DA_TABELA = 1008;

const RESPIRO_DA_LINHA = { pl: 1, pr: 2 } as const;

const TITULOS_DAS_COLUNAS = ['Data', 'Aparelho', 'Tipo de avaliação', 'Valor', 'Unidade', 'Técnica', 'Limite aplicado'];

/** Uma célula da linha: o campo dentro dela, para o leitor de tela anunciar a coluna. */
const Celula: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box role="cell" sx={{ minWidth: 0 }}>
    {children}
  </Box>
);

const destaqueSeFalta = (falta: boolean) =>
  falta ? { '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main', borderWidth: 2 } } : undefined;

export const SecaoMedicoes: React.FC<SecaoMedicoesProps> = ({
  opcoesDeAparelho,
  unidadePadrao,
  desabilitada,
  perigoso,
  insalubre,
  grauPadrao,
  nomeDoRisco,
  faltando,
  formulaDoLimite = null,
}) => {
  const { control, setValue, watch } = useFormContext<FormularioDoRisco>();
  const { fields, append, remove } = useFieldArray({ control, name: 'measurements' });
  const medicoes = watch('measurements');
  const indiceQueConta = indiceDaMedicaoMaisRecente(medicoes);
  const falta = (indice: number, campo: string) => faltando.has(alvoDaMedicao(indice, campo));

  const campoDeTexto = (indice: number, campo: 'unit' | 'technique' | 'appliedToleranceLimit', alvo: string, rotulo: string) => (
    <Controller
      name={`measurements.${indice}.${campo}`}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          id={alvoDaMedicao(indice, alvo)}
          size="small"
          disabled={desabilitada}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          inputProps={{ 'aria-label': rotulo }}
          sx={destaqueSeFalta(falta(indice, alvo))}
        />
      )}
    />
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Conta a medição mais recente. Técnica e limite aplicado são pedidos pelo eSocial e pelo LTCAT.
      </Typography>

      {fields.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          Nenhuma medição registrada.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Box role="table" aria-label="Medições" sx={{ minWidth: LARGURA_MINIMA_DA_TABELA }}>
            <Box
              role="row"
              sx={{ display: 'grid', gridTemplateColumns: COLUNAS, gap: 1, ...RESPIRO_DA_LINHA, py: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}
            >
              {TITULOS_DAS_COLUNAS.map((titulo) => (
                <Typography key={titulo} role="columnheader" variant="caption" fontWeight={700} color="text.secondary">
                  {titulo}
                </Typography>
              ))}
              <Box role="columnheader">
                <Box component="span" sx={visuallyHidden}>
                  Ações
                </Box>
              </Box>
            </Box>
            {fields.map((linha, i) => (
              <Box
                key={linha.id}
                role="row"
                data-testid={`medicao-${i}`}
                sx={{ display: 'grid', gridTemplateColumns: COLUNAS, gap: 1, ...RESPIRO_DA_LINHA, py: 1, alignItems: 'start', borderBottom: 1, borderColor: 'divider' }}
              >
                <Celula>
                  <Controller
                    name={`measurements.${i}.measuredAt`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <DatePicker
                        value={field.value}
                        onChange={field.onChange}
                        disabled={desabilitada}
                        slotProps={{
                          textField: {
                            size: 'small',
                            inputProps: { 'aria-label': 'Data da medição' },
                            error: !!fieldState.error,
                            helperText: fieldState.error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </Celula>
                <Celula>
                  <Controller
                    name={`measurements.${i}.measurementEquipmentId`}
                    control={control}
                    render={({ field }) => (
                      <Autocomplete
                        options={opcoesDeAparelho}
                        disabled={desabilitada}
                        getOptionLabel={(o) => `${o.name}${o.serialNumber ? ` (${o.serialNumber})` : ''}${o.orphaned ? ' (excluído)' : ''}`}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        value={opcoesDeAparelho.find((o) => o.id === field.value) ?? null}
                        onChange={(_, v) => field.onChange(v ? v.id : null)}
                        renderInput={(p) => (
                          <TextField
                            {...p}
                            id={alvoDaMedicao(i, 'equipment')}
                            size="small"
                            inputProps={{ ...p.inputProps, 'aria-label': 'Aparelho' }}
                            sx={destaqueSeFalta(falta(i, 'equipment'))}
                          />
                        )}
                      />
                    )}
                  />
                </Celula>
                <Celula>
                  <Controller
                    name={`measurements.${i}.assessmentType`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        disabled={desabilitada}
                        inputProps={{ 'aria-label': 'Tipo de avaliação' }}
                      >
                        <MenuItem value="">Não informado</MenuItem>
                        {MEASUREMENT_ASSESSMENT_TYPES.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>
                            {MEASUREMENT_ASSESSMENT_TYPE_LABELS[tipo]}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Celula>
                <Celula>
                  <Controller
                    name={`measurements.${i}.value`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        type="number"
                        size="small"
                        id={alvoDaMedicao(i, 'value')}
                        disabled={desabilitada}
                        value={field.value ?? ''}
                        onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        inputProps={{ 'aria-label': 'Valor', step: 'any' }}
                        sx={destaqueSeFalta(falta(i, 'value'))}
                      />
                    )}
                  />
                </Celula>
                <Celula>
                  {campoDeTexto(i, 'unit', 'unit', 'Unidade')}
                </Celula>
                <Celula>
                  {campoDeTexto(i, 'technique', 'technique', 'Técnica')}
                </Celula>
                <Celula>
                  {formulaDoLimite ? (
                    <Typography variant="caption" color="text.secondary">
                      Calculado pelo % de quartzo
                    </Typography>
                  ) : (
                    campoDeTexto(i, 'appliedToleranceLimit', 'limit', 'Limite aplicado')
                  )}
                </Celula>
                <Celula>
                  <IconButton disabled={desabilitada} onClick={() => remove(i)} aria-label="Remover medição">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Celula>
                {formulaDoLimite && (
                  <Box
                    role="cell"
                    id={alvoDaMedicao(i, 'limit')}
                    sx={{ gridColumn: '1 / -1', ...(falta(i, 'limit') ? { outline: 2, outlineStyle: 'solid', outlineColor: 'warning.main', borderRadius: 1, p: 1 } : {}) }}
                  >
                    <Controller
                      name={`measurements.${i}.quartzPercent`}
                      control={control}
                      render={({ field, fieldState }) => (
                        <BlocoDoLimiteCalculado
                          formula={formulaDoLimite}
                          quartzo={field.value ?? ''}
                          onQuartzoChange={field.onChange}
                          resultado={medicoes[i]?.value == null ? '' : String(medicoes[i]?.value)}
                          erro={fieldState.error?.message}
                          disabled={desabilitada}
                        />
                      )}
                    />
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box>
        <Button
          id={ALVO.adicionarMedicao}
          type="button"
          variant="outlined"
          startIcon={<AddIcon />}
          disabled={desabilitada}
          onClick={() => append({ ...NOVA_MEDICAO, unit: unidadePadrao, measuredAt: hojeNoSeletor() })}
        >
          Adicionar medição
        </Button>
      </Box>

      {(insalubre || perigoso) && (
        <Paper variant="outlined" sx={{ p: 2 }} id="risco-enquadramento" tabIndex={-1}>
          <Typography variant="subtitle2">Enquadramento</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Aparece porque {nomeDoRisco} pode gerar {[insalubre && 'insalubridade', perigoso && 'periculosidade'].filter(Boolean).join(' e ')}.
            {indiceQueConta >= 0 ? ' Vale para a medição mais recente.' : ' Adicione uma medição para enquadrar.'}
          </Typography>
          {indiceQueConta >= 0 && (
            <Stack spacing={2}>
              {insalubre && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Controller
                    name={`measurements.${indiceQueConta}.unhealthyFraming`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <Box>
                        <FormControlLabel
                          label="Gera insalubridade (NR-15)"
                          control={
                            <Switch
                              checked={Boolean(field.value)}
                              disabled={desabilitada}
                              onChange={(e) => {
                                field.onChange(e.target.checked);
                                // Grau sem enquadramento é par incompleto; ao marcar, o catálogo entra como sugestão.
                                setValue(
                                  `measurements.${indiceQueConta}.unhealthyDegreeApplied`,
                                  e.target.checked ? grauSugerido(grauPadrao) : '',
                                );
                              }}
                            />
                          }
                        />
                        {fieldState.error && (
                          <Typography variant="caption" color="error" display="block">
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </Box>
                    )}
                  />
                  <Controller
                    name={`measurements.${indiceQueConta}.unhealthyDegreeApplied`}
                    control={control}
                    render={({ field, fieldState }) => (
                      <TextField
                        select
                        label="Grau de insalubridade"
                        size="small"
                        sx={{ minWidth: 220 }}
                        disabled={desabilitada || !medicoes[indiceQueConta]?.unhealthyFraming}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        <MenuItem value="">Escolha o grau</MenuItem>
                        {UNHEALTHY_DEGREES.map((grau) => (
                          <MenuItem key={grau} value={grau}>
                            {OCC_UNHEALTHY_DEGREE_LABELS[grau]}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Stack>
              )}
              {perigoso && (
                <EnquadramentoDaNr16 indice={indiceQueConta} desabilitada={desabilitada} />
              )}
            </Stack>
          )}
        </Paper>
      )}
    </Stack>
  );
};

/** A NR-16 enquadra a atividade em vez de medir: anexo e as quantidades que o laudo cita (#424). */
const EnquadramentoDaNr16: React.FC<{ indice: number; desabilitada: boolean }> = ({ indice, desabilitada }) => {
  const { control, setValue, watch } = useFormContext<FormularioDoRisco>();
  const enquadrada = watch(`measurements.${indice}.hazardousFraming`);

  const numero = (nome: 'storedQuantity' | 'transportedQuantity', rotulo: string) => (
    <Controller
      name={`measurements.${indice}.${nome}`}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          type="number"
          label={rotulo}
          size="small"
          disabled={desabilitada}
          value={field.value ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          inputProps={{ step: 'any' }}
        />
      )}
    />
  );
  const texto = (
    nome: 'storedQuantityUnit' | 'transportedQuantityUnit' | 'riskArea' | 'storageType',
    rotulo: string,
  ) => (
    <Controller
      name={`measurements.${indice}.${nome}`}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          value={field.value ?? ''}
          label={rotulo}
          size="small"
          disabled={desabilitada}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
        />
      )}
    />
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
        <Controller
          name={`measurements.${indice}.hazardousFraming`}
          control={control}
          render={({ field, fieldState }) => (
            <Box>
              <FormControlLabel
                label="A atividade está no anexo da NR-16"
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    disabled={desabilitada}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (!e.target.checked) setValue(`measurements.${indice}.hazardousAnnex`, '');
                    }}
                  />
                }
              />
              {fieldState.error && (
                <Typography variant="caption" color="error" display="block">
                  {fieldState.error.message}
                </Typography>
              )}
            </Box>
          )}
        />
        <Controller
          name={`measurements.${indice}.hazardousAnnex`}
          control={control}
          render={({ field, fieldState }) => (
            <TextField
              select
              label="Anexo da NR-16"
              size="small"
              sx={{ minWidth: 260 }}
              disabled={desabilitada || !enquadrada}
              value={field.value ?? ''}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
            >
              <MenuItem value="">Escolha o anexo</MenuItem>
              {NR16_ANNEXES.map((anexo) => (
                <MenuItem key={anexo} value={anexo}>
                  {anexo}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 1fr 2fr 1fr' }, gap: 2 }}>
        {numero('storedQuantity', 'Quantidade armazenada')}
        {texto('storedQuantityUnit', 'Unidade')}
        {numero('transportedQuantity', 'Quantidade transportada')}
        {texto('transportedQuantityUnit', 'Unidade')}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {texto('riskArea', 'Área de risco')}
        {texto('storageType', 'Tipo de armazenamento')}
      </Box>
    </Stack>
  );
};

export default SecaoMedicoes;
